import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import {FiX} from 'react-icons/fi';
import {useQueryClient} from 'react-query';
import {useLocation, useNavigate} from 'react-router';
import styled from 'styled-components';
import {updateGoal} from '@hooks/goals.hook';
import {useStoreContext} from '@store/store-context';
import {CopyLinkIcon} from '@ui/atoms/icons/copy-link';
import {ItemLoader} from '@ui/organisms/item-loader';
import {Modal} from '@ui/molecules/modal';
import {getColorFromTheme} from '@ui/ui-utils';
import {MemberActivity} from './activity/activity';
import {
  GoalSidecarResults,
  GoalSidecarSummary,
} from './goal/goal-sidecar-details';
import {useInsightHook} from './hooks/insight-hook';
import {updateKPIHistory} from '@utils/firebase-request';
import {canManageGoal, canUpdateGoalResult} from '@utils/goal-permissions';
import {invalidateGoalQueries} from '@utils/goal-query-cache';
import {goalIsEnded} from '@utils/goal-status';

// Kept as shared layout exports for the check-in and meeting comment drawers.
export const CancelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const ItemLoaderWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 75vh;
  align-items: center;
  justify-content: center;
`;

export const ModalCardWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  div.child {
    width: 47%;
  }

  @media (min-width: 800px) and (max-width: 1240px) {
    div.child {
      width: 55%;
    }
  }

  @media (max-width: 800px) {
    justify-content: center;

    div.child {
      min-width: 90%;
      margin: 10% 0;
    }
  }
`;

export const DesktopWrapper = styled.div`
  display: none;

  @media (min-width: 800px) {
    display: block;
  }
`;

export const MobileWrapper = styled.div`
  display: none;

  @media (max-width: 800px) {
    display: block;
  }
`;

const Layer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  pointer-events: auto;
`;

const Sidecar = styled(motion.aside)`
  display: flex;
  width: min(560px, 100vw);
  height: 100vh;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level1};
`;

const SidecarBody = styled.div`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const StickyGoal = styled.div`
  position: sticky;
  z-index: 20;
  top: 0;
  padding: 26px 24px 0;
  background: ${getColorFromTheme('white')};
`;

const Actions = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 26px;
  margin-bottom: 24px;

  &::after {
    position: absolute;
    right: -24px;
    bottom: 0;
    left: -24px;
    height: 1px;
    background: ${getColorFromTheme('borderLight')};
    content: '';
  }
`;

const buttonReset = `
  border: 0;
  font-family: Graphik, sans-serif;
  cursor: pointer;
`;

const BackButton = styled.button`
  ${buttonReset}
  display: inline-flex;
  height: 42px;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font-size: 14px;
  font-weight: 600;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

const CopyButton = styled.button`
  ${buttonReset}
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 2px;
  border-radius: 8px;
  background: transparent;
  color: ${getColorFromTheme('purple300')};
  font-size: 12px;
  font-weight: 500;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${getColorFromTheme('purple100')};
  }
`;

const Copied = styled.span`
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  padding: 8px 10px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  box-shadow: ${({theme}) => theme.elevation.level2};
  white-space: nowrap;
`;

const ScrollSection = styled.div`
  padding: 0 24px;
`;

const SectionBreak = styled.div`
  width: 100%;
  height: 12px;
  background: ${getColorFromTheme('backgroundDark')};
`;

const Activity = styled.div`
  padding: 0 24px 60px;
`;
const Loader = styled.div`
  display: flex;
  min-height: calc(100vh - 120px);
  align-items: center;
  justify-content: center;
`;

export const Insights = observer(() => {
  const {
    goalsState: {setInsightGoal, showInsightGoal},
    authStore: {auth},
    usersStore: {users},
  } = useStoreContext();
  const goalId =
    typeof showInsightGoal.goal === 'string' && showInsightGoal.goal.length > 0
      ? showInsightGoal.goal
      : undefined;
  const {loading, goal, refetch} = useInsightHook(goalId);
  const {saveUpdate} = updateGoal();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [optimisticGoal, setOptimisticGoal] = useState<any>();
  const [stickyTop, setStickyTop] = useState(0);
  const stickyGoalRef = useRef<HTMLDivElement>(null);
  const displayedGoal = optimisticGoal?.id === goal?.id ? optimisticGoal : goal;
  const displayedGoalEnded = goalIsEnded(displayedGoal);
  const canManageDisplayedGoal = displayedGoal
    ? !displayedGoalEnded && canManageGoal(displayedGoal, auth.user.id, users)
    : false;

  useEffect(() => {
    setOptimisticGoal(undefined);
  }, [goal?.id]);

  useLayoutEffect(() => {
    const element = stickyGoalRef.current;
    if (!element) return;

    const updateOffset = () => setStickyTop(element.offsetHeight);
    updateOffset();

    const observer = new ResizeObserver(updateOffset);
    observer.observe(element);

    return () => observer.disconnect();
  }, [displayedGoal?.id]);

  const closeSidecar = useCallback(() => {
    setInsightGoal({goal: '', insight: false});
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('goal');
    searchParams.delete('insight');
    searchParams.delete('isKpi');
    searchParams.delete('status-review');
    const search = searchParams.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, {
      replace: true,
    });
  }, [location.pathname, location.search, navigate, setInsightGoal]);

  useEffect(() => {
    if (!showInsightGoal.insight) return;
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSidecar();
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [closeSidecar, showInsightGoal.insight]);

  const persistGoal = async (nextGoal: any) => {
    const previousCurrentValue = Number(displayedGoal?.currentValue);
    setOptimisticGoal(nextGoal);
    const isKpiValueUpdate = Boolean(nextGoal?.isKpi && nextGoal?.valueUpdated);
    const response = await saveUpdate(
      nextGoal.id,
      isKpiValueUpdate ? {...nextGoal, isUpdate: false} : nextGoal,
    );
    if (!response) {
      setOptimisticGoal(undefined);
      return false;
    }

    const nextCurrentValue = Number(nextGoal?.currentValue);
    if (
      nextGoal?.isKpi &&
      Number.isFinite(nextCurrentValue) &&
      nextCurrentValue !== previousCurrentValue
    ) {
      void updateKPIHistory(nextGoal.id, nextCurrentValue).catch(
        () => undefined,
      );
    }

    await invalidateGoalQueries(queryClient);
    await queryClient.invalidateQueries(['checkin-goals']);
    await refetch();
    setOptimisticGoal(undefined);
    return true;
  };

  const saveGoal = async (nextGoal: any) => {
    if (!canManageDisplayedGoal) return false;
    return persistGoal(nextGoal);
  };

  const saveResult = async (nextGoal: any, resultId: string) => {
    const result = displayedGoal?.keyResults?.find(
      (item: any) => item.id === resultId,
    );
    if (
      displayedGoalEnded ||
      !result ||
      !canUpdateGoalResult(displayedGoal, result, auth.user.id, users)
    ) {
      return false;
    }
    return persistGoal(nextGoal);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return showInsightGoal.insight ? (
    <Modal open fullHeight zIndex={1200} onClose={closeSidecar}>
      <Layer onClick={closeSidecar}>
        <Sidecar
          role="dialog"
          aria-modal="true"
          aria-label={displayedGoal?.name || 'Goal details'}
          initial={{x: '100%'}}
          animate={{x: 0}}
          transition={{type: 'spring', stiffness: 260, damping: 30}}
          onClick={(event) => event.stopPropagation()}>
          <SidecarBody>
            <StickyGoal ref={stickyGoalRef}>
              <Actions>
                <BackButton type="button" onClick={closeSidecar}>
                  <FiX aria-hidden="true" />
                  Back to list
                </BackButton>
                <CopyButton type="button" onClick={copyLink}>
                  {copied && <Copied role="status">Copied</Copied>}
                  <CopyLinkIcon />
                  Copy link
                </CopyButton>
              </Actions>
              {displayedGoal && (
                <GoalSidecarSummary
                  goal={displayedGoal}
                  onSave={saveGoal}
                  canEdit={canManageDisplayedGoal}
                  openStatusMenu={
                    new URLSearchParams(location.search).get(
                      'status-review',
                    ) === 'true'
                  }
                />
              )}
            </StickyGoal>

            {displayedGoal && (
              <>
                <ScrollSection>
                  <GoalSidecarResults
                    goal={displayedGoal}
                    onSave={saveResult}
                    canEditResult={(result) =>
                      !displayedGoalEnded &&
                      canUpdateGoalResult(
                        displayedGoal,
                        result,
                        auth.user.id,
                        users,
                      )
                    }
                  />
                </ScrollSection>
                <SectionBreak aria-hidden="true" />
                <Activity>
                  <MemberActivity
                    goal={displayedGoal as any}
                    stickyTop={stickyTop}
                  />
                </Activity>
              </>
            )}

            {loading && !displayedGoal && (
              <Loader>
                <ItemLoader />
              </Loader>
            )}
          </SidecarBody>
        </Sidecar>
      </Layer>
    </Modal>
  ) : null;
});
